/**
 * The "In progress" badge next to the Fit Score, per the interview-score
 * design spec.
 *
 * Styled after statusPalette.ts's own `active` tone (ink text, transparent
 * background, the default border) rather than a new color -- "in progress"
 * isn't an outcome the way HIRED/REJECTED are, it's the same kind of
 * in-flight state `active` already covers for candidate status elsewhere in
 * this app, and that file's own reasoning for not spending the accent on it
 * applies identically here.
 *
 * The live/stalled distinction is a small teal dot, present and pulsing
 * only while the interview has updated in the last 5 minutes; absent
 * entirely once stalled, so the badge stops claiming activity that isn't
 * happening. Deliberately a standalone component with no data fetching of
 * its own -- the 5-minute threshold is computed by the caller from
 * `updatedAt`, once a real endpoint exposes that field.
 */

import { memo } from 'react';
import styles from './index.module.css';

export type InterviewStatusVariant = 'live' | 'stalled';

export interface InterviewStatusBadgeProps {
  variant: InterviewStatusVariant;
}

export const InterviewStatusBadge = memo(
  ({ variant }: InterviewStatusBadgeProps) => (
    <span className={styles.badge}>
      {variant === 'live' && <span className={styles.dot} aria-hidden="true" />}
      In progress
    </span>
  ),
);

InterviewStatusBadge.displayName = 'InterviewStatusBadge';

export default InterviewStatusBadge;
